import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { DropdownMappingMoreActionsOptionType } from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-more-actions';
import {
  type TrackHierarchyActions,
  type TrackHierarchyLevelOptions,
  TrackHierarchyNumberToLevel,
} from 'lumen/controllers/hotel/settings/central-mapping/hierarchy';
import type { CentralMappingCategory } from 'lumen/models';
import { USER_ENGAGEMENT_ELEMENT_IDS } from 'lumen/models/seen-user-engagement-element';
import type MarketSettingsCentralMappingHierarchyRoute from 'lumen/routes/market/settings/central-mapping/hierarchy';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  type Breakdown,
  BREAKDOWN_TRANSLATIONS,
  CENTRAL_MAPPING_HELP_CENTER_LINKS,
  CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID,
  DEFAULT_BREAKDOWN,
} from 'lumen/utils/central-mapping-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export default class MarketSettingsCentralMappingHierarchyController extends Controller {
  declare model: ModelFrom<MarketSettingsCentralMappingHierarchyRoute>;

  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare centralMapping: Services['central-mapping'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intercom: Services['intercom'];

  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare userEngagement: Services['user-engagement'];

  @tracked breakdown: Breakdown = DEFAULT_BREAKDOWN;
  @tracked categoryToDelete: CentralMappingCategory | null = null;
  @tracked selectedCategories: CentralMappingCategory[] = [];
  @tracked showConfirmationModalDeleteCategory = false;

  MAX_DEPTH = 2;

  updateHierarchyTask = task({ restartable: true }, async (): Promise<void> => {
    const { flattenedHierarchy, breakdown } = this;

    const rootCategoriesIds = this.rootCategories.map(
      (category) => category.id,
    );

    const serializedCategories = flattenedHierarchy
      .filter((category) => !category.isDeleted)
      .map((category) =>
        category.serialize({
          includeId: true,
          // Add isRootCategory to the serialization options if the category is a root category.
          // This is used to remove the parent_id assigned to non-fixed root categories in the get task.
          ...(rootCategoriesIds.includes(category.id) && {
            isRootCategory: true,
          }),
        }),
      );

    const payload = {
      meta: { breakdown },
      data: serializedCategories,
    };

    try {
      const url = formatUrl(
        '/apigateway/v1/app/mapping/categories',
        {
          // @ts-expect-error: Type 'string | null' is not assignable to type 'string | number | boolean | string[] | number[]'.
          market_id: this.hotelAndMarketService.hotelOrMarket.id,
          breakdown,
        },
        this.ajax.host,
      );

      await this.ajax.post(url, { data: payload });

      flattenedHierarchy
        .filter((category) => category.isDeleted)
        .forEach((category) => {
          category.unloadRecord();
        });

      void this.model.hierarchy.perform(breakdown);
      flattenedHierarchy.forEach((item) => item.updateRelationships());

      this.toast.success(
        this.intl.t('settings.central_mapping.hierarchy.toast.success'),
        {
          showIcon: true,
        },
      );
    } catch (error) {
      this.errorTracking.captureException(error);

      // Show retry alert in a different task, otherwise the save button will stay in a loading state while showing the alert.
      void this.centralMapping.showRetryAlertTask.perform(
        this.updateHierarchyTask,
      );
    }
  });

  get flattenedHierarchy(): CentralMappingCategory[] {
    const flattenCategory = (
      category: CentralMappingCategory,
    ): CentralMappingCategory[] => {
      const { children } = category;
      return [category, ...children.slice().flatMap(flattenCategory)];
    };

    const flattenedHierarchyWithDuplicates = this.hierarchy
      .flatMap(flattenCategory)
      .filter((category) => category.depth !== -1);

    const uniqueFlattenedHierarchy = new Set(flattenedHierarchyWithDuplicates);
    return Array.from(uniqueFlattenedHierarchy);
  }

  get hasChanges(): boolean {
    return this.flattenedHierarchy.some(
      (category) =>
        category.hasDirtyAttributes ||
        category.hasDirtyRelationships ||
        category.isDeleted,
    );
  }

  get hierarchy(): CentralMappingCategory[] {
    return this.model.hierarchy.last?.value?.hierarchy ?? [];
  }

  get isLoading(): boolean {
    return this.model.hierarchy.last?.isRunning ?? false;
  }

  get isReadOnly(): boolean {
    return !this.access.hasCentralMappingCategoriesMarketEditAccess;
  }

  get isSaveButtonEnabled(): boolean {
    const isCategoryBeingAdded = this.flattenedHierarchy.some(
      (category) => !category.hasConfirmedName,
    );

    // When a category is new, don't allow saving until a name was confirmed.
    if (isCategoryBeingAdded) {
      return false;
    }

    return this.hasChanges;
  }

  get moreActionsOptions(): DropdownMappingMoreActionsOptionType[] {
    return [
      {
        id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.NAVIGATE_TO_MAPPING,
        label: this.navigateToMappingButtonLabel,
        icon: this.isReadOnly ? undefined : 'edit-pencil',
      },
      {
        id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.HELP_CENTER,
        label: this.intl.t('general.help_center'),
        icon: 'book',
        unsafe_isLink: true,
      },
    ];
  }

  get navigateToMappingButtonLabel(): string {
    if (this.isReadOnly) {
      return this.intl.t('settings.central_mapping.menu.view_mapping');
    }

    return this.intl.t('settings.central_mapping.menu.map_values');
  }

  get rootCategories(): CentralMappingCategory[] {
    return this.flattenedHierarchy.filter(
      (category) => category.depth === 0 && !category.isDeleted,
    );
  }

  get showUserOnboardingModal(): boolean {
    return (
      !this.isLoading &&
      !this.isReadOnly &&
      !this.userEngagement.hasSeen(
        USER_ENGAGEMENT_ELEMENT_IDS.CENTRAL_MAPPING_HIERARCHY_ONBOARDING_MODAL,
      )
    );
  }

  get title(): string {
    const translation = BREAKDOWN_TRANSLATIONS[this.breakdown];
    return translation
      ? `${this.intl.t(translation)} ${this.intl.t('settings.central-mapping.hierarchy')}`
      : '';
  }

  trackHierarchy(
    action: TrackHierarchyActions,
    level: TrackHierarchyLevelOptions,
  ): void {
    this.usageAnalytics.track('Hierarchy Changed', {
      eventTrigger: 'change-hierarchy',
      hierarchy: action,
      level: TrackHierarchyNumberToLevel[level],
    });
  }

  @action closeConfirmationModalDeleteCategory(): void {
    this.showConfirmationModalDeleteCategory = false;
  }

  @action closeUserOnboardingModal(): void {
    this.userEngagement.markAsSeen(
      USER_ENGAGEMENT_ELEMENT_IDS.CENTRAL_MAPPING_HIERARCHY_ONBOARDING_MODAL,
    );
  }

  @action deleteCategory(): void {
    if (!this.categoryToDelete) {
      return;
    }

    // Store depth value before calling `this.deleteCategoryAndChildren`, otherwise `categoryToDelete.hasChildren` is not accurate anymore.
    const categoryDepth = this.categoryToDelete.depth + 1;

    this.deleteCategoryAndChildren(this.categoryToDelete);
    this.showConfirmationModalDeleteCategory = false;
    this.trackCategoryDeleted(categoryDepth);
  }

  @action deleteCategoryAndChildren(category: CentralMappingCategory): void {
    category.undeletedChildren.forEach((child) => {
      this.deleteCategoryAndChildren(child);
    });

    // Also unselect the category if it's selected.
    if (this.selectedCategories.includes(category)) {
      this.selectCategory(category);
    }

    category.deleteRecord();
  }

  @action deleteCategoryWithConfirmationModal(
    category: CentralMappingCategory,
  ): void {
    this.categoryToDelete = category;
    this.showConfirmationModalDeleteCategory = true;
  }

  @action
  discardChanges(): void {
    this.flattenedHierarchy.forEach((category) => {
      category.rollbackAttributes();
      category.rollbackRelationships();
    });
  }

  @action
  async onClickDiscardChanges(): Promise<void> {
    if (this.hasChanges) {
      await this.centralMapping.showUnsavedChangesAlert(() =>
        this.discardChanges(),
      );
    }
  }

  @action onClickMapValues(): void {
    this.router.transitionTo('market.settings.central-mapping.mapping', {
      queryParams: { breakdown: this.breakdown },
    });

    this.usageAnalytics.track('Mapping Page Switched', {
      eventTrigger: 'switch-mapping',
    });
  }

  @action
  onClickSave(): void {
    void this.updateHierarchyTask.perform();
  }

  @action onSelectMoreActionsOption(
    option: DropdownMappingMoreActionsOptionType,
  ): void {
    switch (option.id) {
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.NAVIGATE_TO_MAPPING:
        this.onClickMapValues();
        break;
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.HELP_CENTER:
        this.openHelpArticle();
        break;
      default:
        break;
    }
  }

  @action openHelpArticle(): void {
    this.intercom.redirectToIntercomHelp(
      CENTRAL_MAPPING_HELP_CENTER_LINKS.HIERARCHY_PAGE,
    );
  }

  @action
  selectCategory(category: CentralMappingCategory): void {
    const { selectedCategories } = this;
    const { depth } = category;

    // Replace the whole array to trigger a re-render.
    const newSelectedCategories = [...selectedCategories];

    const trimArray = (
      array: CentralMappingCategory[],
      newSize: number,
    ): void => {
      array.length = newSize;
    };
    const setCategoryAtDepth = (
      array: CentralMappingCategory[],
      cat: CentralMappingCategory,
    ): void => {
      array[cat.depth] = cat;
    };

    const lastSelectedCategory =
      selectedCategories[selectedCategories.length - 1];
    const isSiblingOfLastSelectedCategory =
      lastSelectedCategory?.parent?.undeletedChildren.includes(category) ??
      false;
    const isParentOfLastSelectedCategory = lastSelectedCategory
      ? newSelectedCategories.includes(lastSelectedCategory)
      : false;
    const isLastSelectedCategory = lastSelectedCategory === category;

    if (isLastSelectedCategory) {
      // Unselect the last category.
      trimArray(newSelectedCategories, depth);
    } else if (isSiblingOfLastSelectedCategory) {
      // Unselect category and all of its children.
      trimArray(newSelectedCategories, depth);
      setCategoryAtDepth(newSelectedCategories, category);
    } else if (isParentOfLastSelectedCategory) {
      // Select category and unselect all of its children.
      trimArray(newSelectedCategories, depth + 1);
      setCategoryAtDepth(newSelectedCategories, category);
    } else {
      setCategoryAtDepth(newSelectedCategories, category);
    }

    this.selectedCategories = newSelectedCategories;
  }

  @action trackCategoryAdded(level: number): void {
    this.trackHierarchy('add', level as TrackHierarchyLevelOptions);
  }

  @action trackCategoryDeleted(level: number): void {
    this.trackHierarchy('delete', level as TrackHierarchyLevelOptions);
  }

  @action trackCategoryRenamed(level: number): void {
    this.trackHierarchy('edit', level as TrackHierarchyLevelOptions);
  }
}
